import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Package, Search, X, Filter, ArrowUpDown, Loader2,
  AlertCircle, ChevronLeft, ChevronRight, Warehouse,
  TrendingUp, AlertTriangle, CheckCircle, Calendar, FileText
} from 'lucide-react';
import API_BASE_URL from "../config";
import Swal from 'sweetalert2';

const PAGE_SIZES = [5, 10, 20, 50];
const DEFAULT_PAGE_SIZE = 10;
const LOW_STOCK_THRESHOLD = 5;

const formatDate = (dateString) => {
  if (!dateString) return '—';
  const d = new Date(dateString);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

const aggregateInventory = (inventoryData) => {
  const map = new Map();
  inventoryData.forEach((inv) => {
    const key = `${inv.itemCode}`;
    const isReverted = (inv.remarks || '').toLowerCase().includes('reverted from gr');

    if (!map.has(key)) {
      map.set(key, {
        itemCode: inv.itemCode,
        itemDescription: inv.itemDescription,
        warehouseCode: [],
        totalQty: 0,
        availableQty: 0,
        reservedQty: 0,
        batchMap: new Map(),
        movements: [],
      });
    }

    const entry = map.get(key);
    const qty = Number(inv.quantity) || 0;
    const availQty = Number(inv.availableQty) || 0;

    // ✅ ALWAYS ADD — backend na mismo ang nag-adjust ng quantity
    // Huwag mag double-deduct sa frontend
    entry.totalQty += qty;
    entry.availableQty += availQty;
    entry.reservedQty += Number(inv.reservedQty) || 0;

    if (!entry.warehouseCode.includes(inv.warehouseCode)) {
      entry.warehouseCode.push(inv.warehouseCode);
    }

    const bKey = `${inv.batchNo || 'No Batch'}||${inv.warehouseCode}`;
    if (!entry.batchMap.has(bKey)) {
      entry.batchMap.set(bKey, {
        batchNo: inv.batchNo || 'No Batch',
        warehouseCode: inv.warehouseCode,
        qty: 0,
        expiryDate: inv.expiryDate,
      });
    }
    entry.batchMap.get(bKey).qty += qty;

    // ✅ Tag lang ang movement para makita sa history
    entry.movements.push({
      grNo: inv.poNumber || `INV-${inv.itemCode}`,
      grDate: inv.createdAt || new Date().toISOString(),
      receivedQty: qty,
      batchNo: inv.batchNo || '—',
      expiryDate: inv.expiryDate,
      isReverted: isReverted,
    });
  });

  return Array.from(map.values()).map((item) => ({
    ...item,
    totalQty: Math.max(0, item.totalQty),
    availableQty: Math.max(0, item.availableQty),
    warehouseCode: item.warehouseCode.join(' / '),
    batchEntries: Array.from(item.batchMap.values()),
    batchMap: undefined,
  }));
};

const StockBadge = ({ qty, available }) => {
  if (available <= 0)
    return (
      <span className="inline-flex items-center gap-1 bg-red-50 text-red-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-red-200">
        <AlertTriangle className="w-3.5 h-3.5" /> Out of Stock
      </span>
    );
  if (available <= LOW_STOCK_THRESHOLD)
    return (
      <span className="inline-flex items-center gap-1 bg-amber-50 text-amber-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-amber-200">
        <AlertTriangle className="w-3.5 h-3.5" /> Low Stock
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 bg-emerald-50 text-emerald-700 px-2.5 py-0.5 rounded-full text-xs font-semibold border border-emerald-200">
      <CheckCircle className="w-3.5 h-3.5" /> In Stock
    </span>
  );
};

export default function InventoryList() {
  const [grHeaders, setGrHeaders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncInfo, setSyncInfo] = useState('');
  const syncedRef = useRef(false);
  const [searchText, setSearchText] = useState('');
  const [filterWarehouse, setFilterWarehouse] = useState('');
  const [filterStock, setFilterStock] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [sortKey, setSortKey] = useState('itemCode');
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [grDataForSync, setGrDataForSync] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [allMRs, setAllMRs] = useState([]);
  const [allReturnRequests, setAllReturnRequests] = useState([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [returnHistoryForItem, setReturnHistoryForItem] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // ✅ Loading state for MR usage history in modal
  const [loadingMRHistory, setLoadingMRHistory] = useState(false);
  const [mrHistoryForItem, setMrHistoryForItem] = useState([]);

  useEffect(() => {
    const userData = localStorage.getItem("user");
    if (userData) {
      try { setCurrentUser(JSON.parse(userData)); } catch { }
    }
  }, []);

  useEffect(() => { fetchData(); }, []);

  // ✅ FIXED fetchData — correct endpoint + fetch MRDetails per header
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const invRes = await fetch(`${API_BASE_URL}api/InventoryData`);
      if (!invRes.ok) throw new Error(`Failed to fetch inventory: ${invRes.status}`);
      const invData = await invRes.json();
      setGrHeaders(Array.isArray(invData) ? invData : []);

      // ✅ Fetch MRHeader (no underscore) + MRDetails for each
      try {
        const mrRes = await fetch(`${API_BASE_URL}api/MRHeader`);
        if (mrRes.ok) {
          const mrData = await mrRes.json();
          const mrArray = Array.isArray(mrData) ? mrData : [];

          // ✅ Parallel fetch of all MR details
          const mrWithDetails = await Promise.all(
            mrArray.map(async (mr) => {
              try {
                const detRes = await fetch(`${API_BASE_URL}api/MRDetails/by-header/${mr.id}`);
                if (detRes.ok) {
                  const details = await detRes.json();
                  return { ...mr, mrDetails: Array.isArray(details) ? details : [] };
                }
                return { ...mr, mrDetails: [] };
              } catch {
                return { ...mr, mrDetails: [] };
              }
            })
          );
          setAllMRs(mrWithDetails);
        }
      } catch (err) {
        console.warn('Failed to fetch MRs:', err);
        setAllMRs([]);
      }

    } catch (err) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ✅ Fetch MR usage history for a specific item when modal opens
  const fetchMRHistoryForItem = (item) => {
    setLoadingMRHistory(true);
    // Filter from already-fetched allMRs — match by itemCode only (all warehouses)
    const matched = allMRs.filter(mr =>
      (mr.mrDetails || []).some(d => d.itemCode === item.itemCode)
    );
    setMrHistoryForItem(matched);
    setLoadingMRHistory(false);
  };

  const fetchReturnHistory = async (item) => {
    setLoadingReturns(true);
    setReturnHistoryForItem([]);
    try {
      const res = await fetch(`${API_BASE_URL}api/ReturnRequest`);
      if (res.ok) {
        const allReturns = await res.json();
        setReturnHistoryForItem(allReturns.filter(r => r.itemCode === item.itemCode));
      }
    } catch { setReturnHistoryForItem([]); }
    finally { setLoadingReturns(false); }
  };

  const handleDelete = async (item) => {
    const result = await Swal.fire({
      title: 'Delete Inventory Record?',
      html: `Are you sure you want to delete <strong>${item.itemCode}</strong>?<br><small class="text-red-600">This will delete ALL batches for this item!</small>`,
      icon: 'warning', showCancelButton: true,
      confirmButtonColor: '#dc2626', cancelButtonColor: '#6b7280',
      confirmButtonText: 'Yes, delete it!', cancelButtonText: 'Cancel'
    });
    if (!result.isConfirmed) return;
    setDeletingId(`${item.itemCode}-${item.warehouseCode}`);
    try {
      const fetchResponse = await fetch(`${API_BASE_URL}api/InventoryData?itemCode=${item.itemCode}`);
      if (!fetchResponse.ok) throw new Error('Failed to fetch inventory records');
      const inventoryRecords = await fetchResponse.json();
      if (!inventoryRecords || inventoryRecords.length === 0) throw new Error('No inventory records found');
      let deletedCount = 0, errorCount = 0;
      for (const record of inventoryRecords) {
        try {
          const r = await fetch(`${API_BASE_URL}api/InventoryData/${record.id}`, { method: 'DELETE' });
          if (r.ok) deletedCount++; else errorCount++;
        } catch { errorCount++; }
      }
      if (deletedCount > 0) {
        const user = JSON.parse(localStorage.getItem("user") || '{}');
        await fetch(`${API_BASE_URL}api/Logs`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ action: "DELETE", module: "Inventory", description: `Deleted Item: ${item.itemCode}. ${deletedCount} record(s) deleted.`, userId: user?.userId, username: user?.username, fullName: user?.fullName || user?.username })
        }).catch(() => { });
        Swal.fire({ icon: 'success', title: 'Deleted!', html: `Deleted ${deletedCount} record(s).${errorCount > 0 ? `<br><small class="text-red-600">${errorCount} failed</small>` : ''}`, timer: 3000, showConfirmButton: false });
        fetchData();
      } else throw new Error('Failed to delete any inventory records');
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Delete Failed', text: error.message, confirmButtonColor: '#dc2626' });
    } finally { setDeletingId(null); }
  };

  const inventory = useMemo(() => aggregateInventory(grHeaders), [grHeaders]);
  const warehouseOptions = useMemo(() => [...new Set(inventory.map((i) => i.warehouseCode).filter(Boolean))].sort(), [inventory]);

  const filtered = useMemo(() => {
    let data = [...inventory];
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      data = data.filter((i) => i.itemCode?.toLowerCase().includes(q) || i.itemDescription?.toLowerCase().includes(q) || i.warehouseCode?.toLowerCase().includes(q));
    }
    if (filterWarehouse) data = data.filter((i) => i.warehouseCode === filterWarehouse);
    if (filterStock === 'low') data = data.filter((i) => i.totalQty > 0 && i.totalQty <= LOW_STOCK_THRESHOLD);
    else if (filterStock === 'out') data = data.filter((i) => i.totalQty <= 0);
    data.sort((a, b) => {
      let valA = a[sortKey], valB = b[sortKey];
      if (sortKey === 'totalQty') { valA = Number(valA); valB = Number(valB); }
      else { valA = (valA || '').toString().toLowerCase(); valB = (valB || '').toString().toLowerCase(); }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return data;
  }, [inventory, searchText, filterWarehouse, filterStock, sortKey, sortDir]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const paginated = filtered.slice((safeCurrentPage - 1) * pageSize, safeCurrentPage * pageSize);
  useEffect(() => { setCurrentPage(1); }, [searchText, filterWarehouse, filterStock, pageSize]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortKey(key); setSortDir('asc'); }
  };
  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown className="w-3.5 h-3.5 opacity-30" />;
    return <span className="inline-flex text-xs">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const totalItems = inventory.length;
  const totalUnits = inventory.reduce((s, i) => s + i.totalQty, 0);
  const lowCount = inventory.filter((i) => i.totalQty > 0 && i.totalQty <= LOW_STOCK_THRESHOLD).length;
  const outCount = inventory.filter((i) => i.totalQty <= 0).length;
  const hasActiveFilters = searchText.trim() || filterWarehouse || filterStock;
  const clearFilters = () => { setSearchText(''); setFilterWarehouse(''); setFilterStock(''); };

  const statusConfig = {
    0: { label: 'Draft', color: 'bg-gray-100 text-gray-700 border-gray-200' },
    1: { label: 'Pending', color: 'bg-yellow-50 text-yellow-700 border-yellow-200' },
    2: { label: 'Approved', color: 'bg-blue-50 text-blue-700 border-blue-200' },
    3: { label: 'Complete', color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
    '-1': { label: 'Rejected', color: 'bg-red-50 text-red-700 border-red-200' },
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4 md:p-6">
      <div className="max-w-8xl mx-auto">

        {syncStatus && (
          <div className={`flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-lg mb-4 border ${syncStatus === 'syncing' ? 'bg-slate-50 border-slate-300 text-slate-700' : syncStatus === 'done' ? 'bg-emerald-50 border-emerald-300 text-emerald-800' : 'bg-red-50 border-red-300 text-red-800'}`}>
            {syncStatus === 'syncing' && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {syncStatus === 'done' && <CheckCircle className="w-3.5 h-3.5" />}
            <span>{syncStatus === 'syncing' ? 'Posting to InventoryData…' : `InventoryData synced — ${syncInfo}`}</span>
            {syncStatus === 'done' && <button onClick={() => setSyncStatus(null)} className="ml-auto opacity-50 hover:opacity-100"><X className="w-3.5 h-3.5" /></button>}
          </div>
        )}

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Warehouse className="w-6 h-6 text-slate-600" />Inventory / Current Stock
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Aggregated from all Goods Receipts
                {hasActiveFilters && <span className="ml-2 text-amber-600 font-medium">· Showing {filtered.length} filtered</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={fetchData} className="p-2 rounded-lg border border-gray-300 hover:bg-gray-100 transition text-gray-600" title="Refresh">
                <svg className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </button>
              <button onClick={() => setShowFilters((v) => !v)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-sm font-medium transition ${showFilters || hasActiveFilters ? 'border-slate-400 bg-slate-100 text-slate-700' : 'border-gray-300 text-gray-600 hover:bg-gray-100'}`}>
                <Filter className="w-4 h-4" />Filters
                {hasActiveFilters && <span className="ml-1 bg-slate-600 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">{[searchText.trim(), filterWarehouse, filterStock].filter(Boolean).length}</span>}
              </button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-5">
          {[
            { label: 'Total SKUs', value: totalItems, icon: Package, color: 'slate', sub: 'unique items' },
            { label: 'Total Units', value: totalUnits, icon: TrendingUp, color: 'teal', sub: 'across all warehouses' },
            { label: 'Low Stock', value: lowCount, icon: AlertTriangle, color: 'amber', sub: `≤ ${LOW_STOCK_THRESHOLD} units` },
            { label: 'Out of Stock', value: outCount, icon: AlertTriangle, color: 'red', sub: '0 units remaining' },
          ].map((card, i) => {
            const Icon = card.icon;
            const colors = {
              slate: { bg: 'bg-slate-50', border: 'border-slate-200', icon: 'text-slate-600', val: 'text-slate-900' },
              teal: { bg: 'bg-teal-50', border: 'border-teal-200', icon: 'text-teal-600', val: 'text-teal-900' },
              amber: { bg: 'bg-amber-50', border: 'border-amber-200', icon: 'text-amber-600', val: 'text-amber-900' },
              red: { bg: 'bg-red-50', border: 'border-red-200', icon: 'text-red-600', val: 'text-red-900' },
            };
            const c = colors[card.color];
            return (
              <div key={i} className={`${c.bg} border ${c.border} rounded-xl p-4 shadow-sm`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-gray-600 font-semibold uppercase tracking-wide">{card.label}</span>
                  <Icon className={`w-5 h-5 ${c.icon}`} />
                </div>
                <p className={`text-2xl font-bold ${c.val}`}>{card.value}</p>
                <p className="text-xs text-gray-500 mt-0.5">{card.sub}</p>
              </div>
            );
          })}
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 mb-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" placeholder="Search Item Code / Description…" value={searchText} onChange={(e) => setSearchText(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400" />
              </div>
              <select value={filterWarehouse} onChange={(e) => setFilterWarehouse(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white text-gray-700">
                <option value="">All Warehouses</option>
                {warehouseOptions.map((w) => <option key={w} value={w}>{w}</option>)}
              </select>
              <select value={filterStock} onChange={(e) => setFilterStock(e.target.value)}
                className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-400 bg-white text-gray-700">
                <option value="">All Stock Status</option>
                <option value="low">Low Stock (≤ {LOW_STOCK_THRESHOLD})</option>
                <option value="out">Out of Stock</option>
              </select>
            </div>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="mt-3 text-xs text-red-600 hover:text-red-700 font-semibold flex items-center gap-1">
                <X className="w-3 h-3" /> Clear all filters
              </button>
            )}
          </div>
        )}

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading && (
            <div className="flex flex-col items-center justify-center py-24">
              <Loader2 className="w-10 h-10 text-slate-500 animate-spin mb-3" />
              <p className="text-gray-500 text-sm">Loading inventory…</p>
            </div>
          )}
          {!loading && error && (
            <div className="flex flex-col items-center justify-center py-24">
              <AlertCircle className="w-12 h-12 text-red-400 mb-3" />
              <p className="text-red-600 font-semibold">{error}</p>
              <button onClick={fetchData} className="mt-2 text-sm text-slate-600 underline">Retry</button>
            </div>
          )}
          {!loading && !error && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-24">
              <Package className="w-14 h-14 text-gray-300 mb-3" />
              <p className="text-gray-500 font-medium">{hasActiveFilters ? 'No items match your filters.' : 'No inventory yet.'}</p>
              {hasActiveFilters && <button onClick={clearFilters} className="mt-2 text-sm text-slate-600 underline">Clear filters</button>}
            </div>
          )}
          {!loading && !error && filtered.length > 0 && (
            <>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider w-10">#</th>
                      {[
                        { key: 'itemCode', label: 'Item Code' },
                        { key: 'itemDescription', label: 'Description' },
                        { key: 'warehouseCode', label: 'Warehouse' },
                        { key: 'totalQty', label: 'Qty On Hand' },
                      ].map((col) => (
                        <th key={col.key} onClick={() => handleSort(col.key)}
                          className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider cursor-pointer select-none hover:text-slate-900 transition">
                          <span className="inline-flex items-center gap-1.5">{col.label}<SortIcon col={col.key} /></span>
                        </th>
                      ))}
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Batches</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Status</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-700 uppercase tracking-wider">Movements</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-slate-700 uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginated.map((item, idx) => {
                      const rowNum = (safeCurrentPage - 1) * pageSize + idx + 1;
                      return (
                        <tr key={`${item.itemCode}-${item.warehouseCode}`} className="border-b border-gray-100 hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-3 text-gray-400 font-medium">{rowNum}</td>
                          <td className="px-4 py-3"><span className="font-mono font-semibold text-slate-800">{item.itemCode}</span></td>
                          <td className="px-4 py-3 text-gray-700 max-w-[180px] truncate">{item.itemDescription}</td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 px-2 py-0.5 rounded-md text-xs font-semibold border border-slate-200">
                              <Warehouse className="w-3 h-3" />{item.warehouseCode}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Total:</span>
                                <span className="text-lg font-bold text-gray-900">{item.availableQty}</span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-gray-500">Available:</span>
                                <span className={`text-lg font-bold ${item.totalQty <= 0 ? 'text-red-600' : 'text-green-600'}`}>{item.totalQty}</span>
                              </div>
                              {item.reservedQty > 0 && (
                                <div className="flex items-center gap-2">
                                  <span className="text-xs text-amber-600">Reserved:</span>
                                  <span className="text-sm font-semibold text-amber-700">{item.reservedQty}</span>
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-1">
                              {item.batchEntries.slice(0, 3).map((b, bi) => (
                                <span key={bi} className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-semibold border border-slate-200">
                                  {b.batchNo} <span className="text-slate-500">({b.qty})</span>
                                </span>
                              ))}
                              {item.batchEntries.length > 3 && <span className="text-xs text-gray-400">+{item.batchEntries.length - 3} more</span>}
                            </div>
                          </td>
                          <td className="px-4 py-3"><StockBadge qty={item.totalQty} available={item.availableQty} /></td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center gap-1 bg-gray-100 text-gray-600 px-2 py-0.5 rounded-md text-xs font-semibold border border-gray-200">
                              <FileText className="w-3 h-3" />{item.movements.length} GR{item.movements.length !== 1 ? 's' : ''}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-2">
                              <button
                                onClick={() => {
                                  setSelectedItem(item);
                                  fetchMRHistoryForItem(item);
                                  fetchReturnHistory(item);
                                }}
                                className="inline-flex items-center gap-1.5 bg-slate-700 hover:bg-slate-800 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition">
                                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7s-8.268-2.943-9.542-7z" />
                                </svg>
                                Details
                              </button>
                              {currentUser?.positionId === 'SYSTEM ADMIN' ||
                                currentUser?.position === 'SYSTEM ADMIN' ||
                                currentUser?.role === 'SYSTEM ADMIN' ? (
                                <button
                                  onClick={() => handleDelete(item)}
                                  disabled={deletingId === `${item.itemCode}-${item.warehouseCode}`}
                                  className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold px-3 py-1.5 rounded-lg shadow-sm transition disabled:opacity-50">
                                  {deletingId === `${item.itemCode}-${item.warehouseCode}` ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  )}
                                  Delete
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex flex-col sm:flex-row items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50 gap-3">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <span>Rows per page:</span>
                  <select value={pageSize} onChange={(e) => setPageSize(Number(e.target.value))}
                    className="border border-gray-300 rounded-lg px-2 py-1 bg-white text-sm focus:outline-none">
                    {PAGE_SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <span className="text-gray-400">({(safeCurrentPage - 1) * pageSize + 1}–{Math.min(safeCurrentPage * pageSize, filtered.length)} of {filtered.length})</span>
                </div>
                <div className="flex items-center gap-1">
                  <button disabled={safeCurrentPage === 1} onClick={() => setCurrentPage(1)}
                    className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 hover:bg-gray-100 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 19l-7-7 7-7m8 14l-7-7 7-7" /></svg>
                  </button>
                  <button disabled={safeCurrentPage === 1} onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 hover:bg-gray-100 transition">
                    <ChevronLeft className="w-4 h-4" />
                  </button>
                  {(() => {
                    const pages = []; const range = 2;
                    let start = Math.max(1, safeCurrentPage - range);
                    let end = Math.min(totalPages, safeCurrentPage + range);
                    if (start > 1) { pages.push(1); if (start > 2) pages.push('...'); }
                    for (let i = start; i <= end; i++) pages.push(i);
                    if (end < totalPages) { if (end < totalPages - 1) pages.push('...'); pages.push(totalPages); }
                    return pages.map((p, i) =>
                      p === '...' ? <span key={`e${i}`} className="px-1 text-gray-400 text-sm">…</span> : (
                        <button key={p} onClick={() => setCurrentPage(p)}
                          className={`w-8 h-8 rounded-md text-sm font-semibold transition ${p === safeCurrentPage ? 'bg-slate-700 text-white shadow-sm' : 'border border-gray-300 bg-white text-gray-600 hover:bg-gray-100'}`}>{p}</button>
                      )
                    );
                  })()}
                  <button disabled={safeCurrentPage === totalPages} onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 hover:bg-gray-100 transition">
                    <ChevronRight className="w-4 h-4" />
                  </button>
                  <button disabled={safeCurrentPage === totalPages} onClick={() => setCurrentPage(totalPages)}
                    className="p-1.5 rounded-md border border-gray-300 bg-white text-gray-600 disabled:opacity-30 hover:bg-gray-100 transition">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" /></svg>
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════
          DETAIL MODAL
          ═══════════════════════════════════════ */}
      {selectedItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[88vh] overflow-hidden flex flex-col">
            <div className="bg-gradient-to-r from-indigo-700 to-indigo-600 text-white px-6 py-4 flex-shrink-0 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5" />
                <div>
                  <h3 className="text-lg font-bold font-mono">{selectedItem.itemCode}</h3>
                  <p className="text-slate-300 text-xs">{selectedItem.itemDescription}</p>
                </div>
              </div>
              <button onClick={() => setSelectedItem(null)} className="bg-white/10 hover:bg-white/20 rounded-lg p-1.5 transition"><X className="w-5 h-5" /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {/* Summary cards */}
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-teal-700 font-semibold uppercase mb-1">Total On Hand</p>
                  <p className="text-2xl font-bold text-teal-900">{selectedItem.totalQty}</p>
                  <p className="text-xs text-teal-500">units</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-700 font-semibold uppercase mb-1">Batches</p>
                  <p className="text-2xl font-bold text-slate-900">{selectedItem.batchEntries.length}</p>
                  <p className="text-xs text-slate-500">tracked</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-700 font-semibold uppercase mb-1">Warehouse</p>
                  <p className="text-lg font-bold text-slate-900">{selectedItem.warehouseCode}</p>
                  <p className="text-xs text-slate-500">location</p>
                </div>
              </div>

              {/* Batch Breakdown */}
              <div className="mb-5">
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2"><Package className="w-4 h-4 text-slate-600" /> Batch Breakdown</h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">#</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">Batch / Lot No</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">Warehouse</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-right text-xs font-bold text-slate-700 uppercase">Qty</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">Expiry Date</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItem.batchEntries.map((b, i) => {
                        const isExpired = b.expiryDate && new Date(b.expiryDate) < new Date();
                        const isNearExpiry = b.expiryDate && !isExpired && (new Date(b.expiryDate) - new Date()) < 30 * 24 * 60 * 60 * 1000;
                        return (
                          <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-slate-50 transition-colors">
                            <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                            <td className="px-4 py-2 font-semibold text-gray-800">{b.batchNo}</td>
                            <td className="px-4 py-2 text-xs text-gray-600">{b.warehouseCode || '—'}</td>
                            <td className="px-4 py-2 text-right font-bold text-teal-700">{b.qty}</td>
                            <td className="px-4 py-2 text-gray-600 text-xs">{formatDate(b.expiryDate)}</td>
                            <td className="px-4 py-2">
                              {isExpired ? <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded text-xs font-semibold border border-red-200">Expired</span>
                                : isNearExpiry ? <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded text-xs font-semibold border border-amber-200">Near Expiry</span>
                                  : b.expiryDate ? <span className="bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded text-xs font-semibold border border-emerald-200">Valid</span>
                                    : <span className="text-gray-400 text-xs">—</span>}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Stock Movement History */}
              <div className="mb-5">
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-slate-600" /> Stock Movement History
                  <span className="bg-slate-100 text-slate-700 text-xs font-bold px-2 py-0.5 rounded-full border border-slate-200">{selectedItem.movements.length} entries</span>
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">#</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">GR No</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">GR Date</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-right text-xs font-bold text-slate-700 uppercase">Qty Received</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">Batch</th>
                        <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-slate-700 uppercase">Expiry</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedItem.movements.map((m, i) => (
                        <tr key={i} className="border-b border-gray-100 last:border-0 hover:bg-slate-50 transition-colors">
                          <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                          <td className="px-4 py-2"><span className="font-mono font-semibold text-slate-800 text-xs">{m.grNo}</span></td>
                          <td className="px-4 py-2 text-gray-600 text-xs flex items-center gap-1"><Calendar className="w-3 h-3 text-gray-400" />{formatDate(m.grDate)}</td>
                          <td className="px-4 py-2 text-right font-bold">
                            {m.isReverted
                              ? <span className="text-red-600">-{m.receivedQty}</span>
                              : <span className="text-teal-700">+{m.receivedQty}</span>
                            }
                          </td>                          <td className="px-4 py-2">{m.batchNo !== '—' ? <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded text-xs font-semibold border border-slate-200">{m.batchNo}</span> : <span className="text-gray-400 text-xs">—</span>}</td>
                          <td className="px-4 py-2 text-gray-500 text-xs">{formatDate(m.expiryDate)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* ✅ MATERIAL REQUEST USAGE HISTORY — FIXED */}
              <div className="mb-5">
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <FileText className="w-4 h-4 text-blue-600" /> Material Request Usage History
                  <span className="bg-blue-100 text-blue-700 text-xs font-bold px-2 py-0.5 rounded-full border border-blue-200">
                    {loadingMRHistory ? '...' : `${mrHistoryForItem.length} MRs`}
                  </span>
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {loadingMRHistory ? (
                    <div className="flex items-center justify-center gap-2 py-8 text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />Loading MR history...
                    </div>
                  ) : mrHistoryForItem.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No Material Requests yet for this item</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-blue-50">
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-blue-700 uppercase">#</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-blue-700 uppercase">Control No</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-blue-700 uppercase">Date</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-blue-700 uppercase">Requested By</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-blue-700 uppercase">Department</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-blue-700 uppercase">Warehouse</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-right text-xs font-bold text-blue-700 uppercase">Qty Requested</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-center text-xs font-bold text-blue-700 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {mrHistoryForItem.map((mr, i) => {
                          // ✅ Find the specific detail row for this item
                          const detail = (mr.mrDetails || []).find(d => d.itemCode === selectedItem.itemCode);
                          const sc = statusConfig[String(mr.status)] || statusConfig[0];
                          return (
                            <tr key={mr.id || i} className="border-b border-gray-100 last:border-0 hover:bg-blue-50 transition-colors">
                              <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                              <td className="px-4 py-2">
                                <span className="font-mono font-semibold text-blue-800 text-xs">{mr.controlNo || mr.mrNo}</span>
                              </td>
                              <td className="px-4 py-2 text-gray-600 text-xs">
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3 text-gray-400" />{formatDate(mr.mrDate)}</span>
                              </td>
                              <td className="px-4 py-2 text-gray-700 text-xs">{mr.requestedByName || '—'}</td>
                              <td className="px-4 py-2 text-gray-700 text-xs">{mr.department || mr.departmentName || '—'}</td>
                              <td className="px-4 py-2 text-gray-600 text-xs">{detail?.warehouseCode || '—'}</td>
                              <td className="px-4 py-2 text-right">
                                <span className="font-bold text-red-600">-{detail?.requestedQty || 0}</span>
                              </td>
                              <td className="px-4 py-2 text-center">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${sc.color}`}>
                                  {sc.label}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>

              {/* Return History */}
              <div>
                <h4 className="text-sm font-bold text-gray-800 mb-2 flex items-center gap-2">
                  <svg className="w-4 h-4 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Return History
                  <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full border border-orange-200">
                    {returnHistoryForItem.length} Returns
                  </span>
                </h4>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  {loadingReturns ? (
                    <div className="flex items-center justify-center gap-2 py-8 text-gray-500 text-sm">
                      <Loader2 className="w-4 h-4 animate-spin" />Loading return history...
                    </div>
                  ) : returnHistoryForItem.length === 0 ? (
                    <div className="p-8 text-center text-gray-400">
                      <p className="text-sm">No return history for this item</p>
                    </div>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-orange-50">
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-orange-700 uppercase">#</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-orange-700 uppercase">Date</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-orange-700 uppercase">MR Control No</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-orange-700 uppercase">Returned By</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-right text-xs font-bold text-orange-700 uppercase">Return Qty</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-left text-xs font-bold text-orange-700 uppercase">Reason</th>
                          <th className="border-b border-gray-200 px-4 py-2 text-center text-xs font-bold text-orange-700 uppercase">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {returnHistoryForItem.map((ret, i) => (
                          <tr key={ret.id || i} className="border-b border-gray-100 last:border-0 hover:bg-orange-50 transition-colors">
                            <td className="px-4 py-2 text-gray-400">{i + 1}</td>
                            <td className="px-4 py-2 text-gray-600 text-xs">{formatDate(ret.requestedDate)}</td>
                            <td className="px-4 py-2"><span className="font-mono font-semibold text-blue-800 text-xs">{ret.mrControlNo || ret.mrHeaderId || '—'}</span></td>
                            <td className="px-4 py-2 text-gray-700 text-xs">{ret.requestedByName || '—'}</td>
                            <td className="px-4 py-2 text-right font-bold text-green-600">+{ret.requestedReturnQty}</td>
                            <td className="px-4 py-2 text-gray-600 text-xs max-w-[150px] truncate">{ret.returnReason || '—'}</td>
                            <td className="px-4 py-2 text-center">
                              {ret.status === 1
                                ? <span className="bg-green-50 text-green-700 px-2 py-0.5 rounded-full text-xs font-bold border border-green-200">Approved</span>
                                : ret.status === -1
                                  ? <span className="bg-red-50 text-red-700 px-2 py-0.5 rounded-full text-xs font-bold border border-red-200">Rejected</span>
                                  : <span className="bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full text-xs font-bold border border-amber-200">Pending</span>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end px-6 py-3 border-t border-gray-200 bg-gray-50 flex-shrink-0">
              <button onClick={() => setSelectedItem(null)} className="text-sm text-gray-500 hover:text-gray-700 font-medium transition">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}